import React, { useState } from "react";
import { PageFormLayout, FormActions } from "./ui";
import { useNavigate } from "react-router-dom";
import { api } from "../services";
import { useTranslation } from "react-i18next";
import NativeInput from "./NativeInput";
import { toast } from "react-toastify";
import { invalidateSetupGuideCache } from "../hooks/useSetupGuideStatus";

function AddNewStore() {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const [storeName, setStoreName] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ name: "", email: "" });
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const isValidEmail = (val) => /\S+@\S+\.\S+/.test(val);

  const validateAll = () => {
    const errs = { name: "", email: "" };
    if (!storeName.trim()) errs.name = t("Store name is required");
    if (!email.trim()) errs.email = t("Email is required");
    else if (!isValidEmail(email.trim()))
      errs.email = t("Please enter a valid email address");
    setFieldErrors(errs);
    return !errs.name && !errs.email;
  };

  const handleSave = async () => {
    if (!validateAll()) {
      setError(t("Please fix the errors above before saving."));
      return;
    }

    try {
      setIsLoading(true);
      setFieldErrors({ name: "", email: "" });
      setError("");

      const token = localStorage.getItem("token");
      await api.post(
        "/stores",
        {
          name: storeName.trim(),
          email: email.trim().toLowerCase(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success(t("Store added successfully."));
      invalidateSetupGuideCache();
      navigate("/store&branches-overview");
    } catch (err) {
      const message =
        err.response?.data?.message ||
        t("Failed to add store. Please try again.");
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/store&branches-overview");
  };

  return (
    <PageFormLayout rootClass="add-store" title={t("addnewstore")}>
      {error && (
        <div className="alert alert-danger oh-form-alert" role="alert">
          {error}
        </div>
      )}

      <div className="oh-form-grid">
        <NativeInput
          label={t("storename")}
          required
          tooltip={t("formTooltips.storeName")}
          id="storeName"
          type="text"
          value={storeName}
          onChange={(e) => {
            setStoreName(e.target.value);
            setError("");
            setFieldErrors((prev) => ({
              ...prev,
              name: e.target.value.trim() ? "" : t("Store name is required"),
            }));
          }}
          invalid={!!fieldErrors.name}
          feedback={fieldErrors.name}
        />
        <NativeInput
          label={t("emailaddress")}
          required
          tooltip={t("formTooltips.storeEmail")}
          id="email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError("");
            const val = e.target.value.trim();
            setFieldErrors((prev) => ({
              ...prev,
              email: !val
                ? t("Email is required")
                : isValidEmail(val)
                  ? ""
                  : t("Please enter a valid email address"),
            }));
          }}
          invalid={!!fieldErrors.email}
          feedback={fieldErrors.email}
        />
      </div>

      <FormActions
        isRtl={isArabic}
        cancelLabel={t("cancel")}
        saveLabel={t("savestore")}
        savingLabel={t("saving")}
        onCancel={handleCancel}
        onSave={handleSave}
        saving={isLoading}
      />
    </PageFormLayout>
  );
}

export default AddNewStore;
